import { ApolloLink } from '@apollo/client';
import { ServerParseError } from '@apollo/client/errors';
import Decimal from 'decimal.js';
import { buildSchema, GraphQLError } from 'graphql';
import { isLosslessNumber, parse, stringify } from 'lossless-json';
import { defer, map } from 'rxjs';

import { safeDecimal } from 'utils/decimalUtils';

import schemaString from 'store/apollo/schema.graphql';

import { mockSchemaStrings } from './mockSchemas';
import { compileResponsePlan, convertResponse, serializeVariables } from './responsePlan';

const schema = buildSchema([schemaString, ...mockSchemaStrings].join('\n'));

/**
 * Offending values already reported this session, so one bad list cannot flood Datadog.
 */
const reportedParseFailures = new Set<string>();

/**
 * A BigDecimal the parser rejects is still returned as null, but the generated types declare a
 * non-null BigDecimal field as `Decimal`. Reporting it is how we find out whether that gap is
 * theoretical or something production actually hits. Logging is imported lazily so the Apollo link
 * layer keeps no static dependency on the logging bundle, mirroring `reportMapsError`.
 *
 * @param value - The wire value that failed to convert.
 */
const reportUnparseableBigDecimal = (value: number | string) => {
  const key = `${typeof value}:${String(value)}`;

  if (reportedParseFailures.has(key)) {
    return;
  }

  reportedParseFailures.add(key);

  void import('components/core/logging/LoggingService')
    .then(({ default: LoggingService }) => {
      LoggingService.captureException(new Error(`BigDecimal scalar could not be parsed: ${JSON.stringify(value)}`), {
        info: 'graphql-scalar-parse-failure',
        scalar: 'BigDecimal',
        value: String(value),
        valueType: typeof value,
      });
    })
    .catch(() => {
      // Best-effort logging: never let a failed dynamic import surface as an unhandled rejection.
    });
};

const typesMap = {
  BigDecimal: {
    parseValue: (value: unknown) => {
      if (typeof value === 'number') {
        throw new GraphQLError('BigDecimal numeric response lacks raw JSON precision');
      }

      if (typeof value === 'string') {
        const decimal = safeDecimal(value, { allowInfinite: false });

        if (decimal === null) {
          reportUnparseableBigDecimal(value);
        }

        return decimal;
      }

      return value;
    },
    serialize: (value: unknown) => {
      if (Decimal.isDecimal(value)) {
        if (!value.isFinite()) {
          throw new GraphQLError('BigDecimal input must be finite');
        }

        return value.toString();
      }

      if (
        (typeof value === 'number' && !Number.isFinite(value)) ||
        (typeof value === 'string' && safeDecimal(value, { allowInfinite: false }) === null)
      ) {
        throw new GraphQLError('BigDecimal input must be a finite decimal');
      }

      return value;
    },
  },
};

export const scalarLink = new ApolloLink((operation, forward) =>
  defer(() => {
    const plan = compileResponsePlan({ operation, schema });
    const localOperation: ApolloLink.Operation = Object.defineProperties(
      {
        ...operation,
        extensions: { ...operation.extensions },
        variables: serializeVariables({ decimal: typesMap.BigDecimal.serialize, operation, schema }),
      },
      {
        client: { enumerable: false, value: operation.client },
        getContext: { enumerable: false, value: operation.getContext },
        setContext: { enumerable: false, value: operation.setContext },
      }
    );

    return forward(localOperation).pipe(
      map(result => {
        if (!result.data) {
          return result;
        }

        try {
          return {
            ...result,
            data: convertResponse({
              data: result.data,
              decimal: typesMap.BigDecimal.parseValue,
              mode: 'domain',
              plan,
              schema,
            }),
          };
        } catch (error) {
          if (error instanceof GraphQLError) {
            return { errors: [...(result.errors ?? []), error] };
          }

          throw error;
        }
      })
    );
  })
);

type BigDecimalResponseFetchArgs = {
  /**
   * Preserves the existing request instrumentation and transport options.
   */
  fetch: typeof fetch;
  /**
   * Retains the full document when persisted queries omit it from the request.
   */
  operation: ApolloLink.Operation;
};

type NormalizeResponseArgs = {
  /**
   * Contains numeric lexemes before native JSON parsing.
   */
  bodyText: string;
  /**
   * Selects scalar types using the operation's declared output fields.
   */
  operation: ApolloLink.Operation;
  /**
   * Identifies the original response in Apollo parse errors.
   */
  response: Response;
};

const isResponseObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && !isLosslessNumber(value);

const normalizeBigDecimalResponse = ({ bodyText, operation, response }: NormalizeResponseArgs): string => {
  let result: unknown;

  try {
    result = parse(bodyText, undefined, { onDuplicateKey: ({ newValue }) => newValue });
  } catch (error) {
    throw new ServerParseError(error, { bodyText, response });
  }

  if (!isResponseObject(result) || 'hasNext' in result || 'incremental' in result) {
    throw new Error('Unsupported GraphQL response format');
  }

  if (!Object.hasOwn(result, 'data') && !Object.hasOwn(result, 'errors')) {
    return bodyText;
  }

  if (result.errors != null && !Array.isArray(result.errors)) {
    throw new Error('GraphQL response errors must be an array or null');
  }

  if (result.data == null) {
    return bodyText;
  }

  if (!isResponseObject(result.data)) {
    throw new Error('GraphQL response data must be an object or null');
  }

  const plan = compileResponsePlan({ operation, schema });
  const data = convertResponse({
    data: result.data,
    mode: 'wire',
    plan,
    schema,
  });

  const text = stringify({ ...result, data });
  if (text === undefined) {
    throw new Error('GraphQL response could not be serialized');
  }

  return text;
};

/**
 * Ends lossless token lifetime before Apollo response links and cache writes.
 */
export const createBigDecimalResponseFetch =
  ({ fetch: fetcher, operation }: BigDecimalResponseFetchArgs): typeof fetch =>
  async (input, init) => {
    const response = await fetcher(input, init);
    if (
      response.status >= 300 &&
      !response.headers.get('content-type')?.includes('application/graphql-response+json')
    ) {
      return response;
    }

    const readText = response.text.bind(response);
    Object.defineProperty(response, 'text', {
      value: async () => normalizeBigDecimalResponse({ bodyText: await readText(), operation, response }),
    });

    return response;
  };
